import { ComponentPortal } from '@angular/cdk/portal';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, inject } from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
} from '@angular/router';

import { BehaviorSubject, filter, tap } from 'rxjs';

import { StringHandler } from '@alan-apps/utils';
import { CdkService } from '@nghedgehog/core';

import { DocumentHeaderService } from '../../services/document-header.service';
import { RouteLoadingComponent } from './route-loading.component';
import { ROUTE_LOADING_TOKEN } from './route-loading.model';
import { useOnBrowser } from '../../utils';
import { clearI18nString } from '../../pipes/i18n-link/getI18nRouteLink';

@Injectable({
  providedIn: 'root',
})
export class RouteLoadingService {
  private _router = inject(Router);
  private _cdk = inject(CdkService);
  private _activatedRoute = inject(ActivatedRoute);
  private _title = inject(DocumentHeaderService);
  private document = inject<Document>(DOCUMENT);
  private portalHost = this._cdk.createBodyPortalHost();

  currentUrl = new BehaviorSubject<string>('');

  private _innerContent!: string;
  public get innerContent(): string {
    return this._innerContent;
  }
  public set innerContent(value: string) {
    this._innerContent = value;
  }

  constructor() {
    const event = new CustomEvent('app-ready', {
      bubbles: true,
      detail: (value: string) => (this.innerContent = value),
    });

    this.document.dispatchEvent(event);

    // never cancel when app is alive
    this._router.events
      .pipe(
        // because ssr so first time skip show router blockView
        // .skipWhile((event: RouterEvent) => event.id < 2)
        filter(
          // TODO: fix this
          (event: any) =>
            event['id'] !== undefined && event['state'] === undefined,
        ),
        tap((event) => {
          this.navigationInterceptor(event as never);
        }),
      )
      .subscribe();
  }

  private navigationInterceptor(event: RouterEvent): void {
    let isChangeUrl = false;

    if (event instanceof NavigationStart) {
      isChangeUrl = true;
      this.block();
    } else if (
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError
    ) {
      isChangeUrl = true;
      this.unblock();
      this.changeTitle();
      this._title.canonical(
        location.origin + clearI18nString(location.pathname),
      );
    }

    if (isChangeUrl) {
      this.currentUrl.next(new StringHandler(event.url).getPath());
    }
  }

  private block = useOnBrowser(() => {
    if (!this.portalHost.hasAttached()) {
      const component = new ComponentPortal(
        RouteLoadingComponent,
        undefined,
        this._cdk.createInjector(ROUTE_LOADING_TOKEN, this.innerContent),
      );
      this.portalHost.attach(component);
    }
  });

  private unblock = useOnBrowser(() => {
    if (this.portalHost.hasAttached()) this.portalHost.detach();
  });

  private changeTitle() {
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    const currentData = route.snapshot?.data;

    if (route.outlet === 'primary' && currentData) {
      let title = currentData['title'];
      let description = currentData['description'];

      const parent = route.snapshot.parent;
      if (!title && parent) {
        title = parent.data['title'];
        description = parent.data['description'];
      }

      if (!title) {
        const data = currentData['data'];
        title = data ? data[currentData['dataTitleKey'] ?? 'title'] : '';

        if (!description) {
          description = data
            ? data[currentData['dataDescriptionKey'] ?? 'description']
            : '';
        }
      }
      if (title) {
        this._title.setInfo({ title, description }, { disabledExtends: true });
      }
    }
  }
}
